import { IconFileText, IconLayoutGrid, IconStar } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, Loader, Text } from "@/components/ui";
import { toastErrorApi } from "@/lib/toast";
import { cargarPredefinido, cargarTemplate, type RepDefinicion } from "../services/reporteria.api";
import { usePredefinidos, useTemplates } from "../hooks/use-reporteria";

/**
 * Galería de reportes guardados (como el reporteador legado): las plantillas del
 * usuario y los reportes predefinidos de sus configuraciones se muestran como
 * TARJETAS; al elegir una se carga en el builder.
 */
export function GaleriaReportes({
  open,
  onClose,
  onCargar,
}: {
  open: boolean;
  onClose: () => void;
  onCargar: (def: RepDefinicion) => void;
}) {
  const templates = useTemplates();
  const predefinidos = usePredefinidos();

  const cargarTpl = useMutation({
    mutationFn: cargarTemplate,
    onSuccess: (def) => {
      onCargar(def);
      onClose();
    },
    onError: toastErrorApi,
  });
  const cargarPre = useMutation({
    mutationFn: cargarPredefinido,
    onSuccess: (def) => {
      onCargar(def);
      onClose();
    },
    onError: toastErrorApi,
  });

  const cargando = cargarTpl.isPending || cargarPre.isPending;
  const sinNada =
    !templates.isLoading &&
    !predefinidos.isLoading &&
    (templates.data?.length ?? 0) === 0 &&
    (predefinidos.data?.length ?? 0) === 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Abrir un reporte guardado</DialogTitle>
        </DialogHeader>

        {templates.isLoading || predefinidos.isLoading ? (
          <div className="flex justify-center py-8">
            <Loader />
          </div>
        ) : sinNada ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <IconLayoutGrid size={28} className="text-muted-foreground" />
            <Text size="sm" c="dimmed">
              Todavía no tienes plantillas ni reportes predefinidos. Arma uno y guárdalo como plantilla.
            </Text>
          </div>
        ) : (
          <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1">
            {(predefinidos.data?.length ?? 0) > 0 && (
              <section>
                <Text fw={600} size="sm" className="mb-1.5">
                  Reportes predefinidos
                </Text>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {predefinidos.data!.map((p) => (
                    <button
                      key={`p-${p.id}`}
                      type="button"
                      disabled={cargando}
                      onClick={() => cargarPre.mutate(p.id)}
                      className="flex flex-col gap-1 rounded-md border border-border p-3 text-left transition-colors hover:border-multiva-400 hover:bg-multiva-50 disabled:opacity-60 dark:hover:bg-multiva-900/20"
                    >
                      <IconStar size={18} className="text-multiva-600" />
                      <span className="truncate text-sm font-medium">{p.nombre}</span>
                      {p.descripcion && (
                        <span className="line-clamp-2 text-xs text-muted-foreground">{p.descripcion}</span>
                      )}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {(templates.data?.length ?? 0) > 0 && (
              <section>
                <Text fw={600} size="sm" className="mb-1.5">
                  Mis plantillas
                </Text>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {templates.data!.map((t) => (
                    <button
                      key={`t-${t.id}`}
                      type="button"
                      disabled={cargando}
                      onClick={() => cargarTpl.mutate(t.id)}
                      className="flex flex-col gap-1 rounded-md border border-border p-3 text-left transition-colors hover:border-multiva-400 hover:bg-multiva-50 disabled:opacity-60 dark:hover:bg-multiva-900/20"
                    >
                      <IconFileText size={18} className="text-multiva-600" />
                      <span className="truncate text-sm font-medium">{t.nombre}</span>
                      {t.descripcion && (
                        <span className="line-clamp-2 text-xs text-muted-foreground">{t.descripcion}</span>
                      )}
                    </button>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
